import React from 'react';
import { Clock, Mail, MessageSquare, BarChart3, Palette, BookOpen, Phone } from 'lucide-react';

const painPoints = [
  {
    icon: Phone,
    problem: "Missing calls while you work?",
    example: "Taxi driver mid-fare. Gardener with muddy hands. That ringing phone is money walking away.",
    solution: "24/7 chatbot captures every lead. You never miss a booking again.",
    color: '#D4AF37',
    type: 'business'
  },
  {
    icon: MessageSquare,
    problem: "Same questions, twenty times a day?",
    example: "Answering booking queries instead of serving customers.",
    solution: "Salon saved 14 hours weekly. Filled 6 cancelled slots monthly.",
    color: '#C41E3A',
    type: 'business'
  },
  {
    icon: Clock,
    problem: "Hours lost writing listings?",
    example: "Every new property takes 45 minutes to write and post.",
    solution: "Estate agent now handles 40% more listings. 15 hours back weekly.",
    color: '#2E7D32',
    type: 'business'
  },
  {
    icon: BookOpen,
    problem: "Dissertation deadline looming?",
    example: "You've got the research. Turning it into something polished feels impossible.",
    solution: "50 pages of notes became a 20-slide presentation. On time.",
    color: '#D4AF37',
    type: 'individual'
  },
  {
    icon: BarChart3,
    problem: "Social media guilt?",
    example: "You know you should post. It's Friday. You haven't.",
    solution: "Café owner reclaimed 12 hours weekly. 9% revenue bump.",
    color: '#C41E3A',
    type: 'business'
  },
  {
    icon: Palette,
    problem: "Want something custom-made?",
    example: "T-shirts, artwork, gifts — but design feels too technical.",
    solution: "Photo to professional product. No design skills needed.",
    color: '#D4AF37',
    type: 'individual'
  },
  {
    icon: Mail,
    problem: "Drowning in emails?",
    example: "2 hours daily answering the same client questions.",
    solution: "Accountancy firm saved 24 hours weekly. That's £37,440 in billable hours.",
    color: '#C0C0C0',
    type: 'business'
  }
];

const PainPointsSection = () => {
  return (
    <section className="py-16 px-4" style={{ background: '#1a1a1a' }}>
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4" style={{ color: '#FFFFFF' }}>Sound Familiar?</h2>
          <p className="body-large" style={{ color: '#C0C0C0', maxWidth: '800px', margin: '0 auto' }}>
            Real problems. Real solutions. Quiet efficiency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {painPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <div
                key={index}
                className="p-6 hover-lift"
                style={{
                  background: '#2D2D2D',
                  borderRadius: '1rem',
                  border: `2px solid ${point.color}`,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Accent bar */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: point.color
                  }}
                />
                
                <div className="flex items-start gap-3 mb-4">
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: `${point.color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      border: `2px solid ${point.color}`
                    }}
                  >
                    <Icon size={24} color={point.color} />
                  </div>
                  <div className="flex-1">
                    <h3 
                      className="heading-3 mb-2" 
                      style={{ 
                        fontSize: '1rem',
                        lineHeight: '1.4',
                        color: '#FFFFFF'
                      }}
                    >
                      {point.problem}
                    </h3>
                  </div>
                </div>

                <p 
                  className="body-small mb-4" 
                  style={{ 
                    color: '#A0A0A0',
                    fontStyle: 'italic',
                    paddingLeft: '0.5rem',
                    borderLeft: `2px solid ${point.color}30`
                  }}
                >
                  "{point.example}"
                </p>

                <div
                  style={{
                    background: `${point.color}15`,
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    marginTop: '1rem'
                  }}
                >
                  <p 
                    className="body-small" 
                    style={{ 
                      color: '#FFFFFF',
                      fontWeight: '500',
                      lineHeight: '1.5'
                    }}
                  >
                    ✓ {point.solution}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <p className="body-large" style={{ color: '#FFFFFF', fontWeight: '500' }}>
            Average results: <span style={{ color: '#C41E3A' }}>551% ROI</span> • 
            <span style={{ color: '#4CAF50' }}> 2.3 month payback</span> • 
            <span style={{ color: '#D4AF37' }}> 10-24 hours saved weekly</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PainPointsSection;
